
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import Button from './Button';
import { useWaitlist } from '../lib/useWaitlist';

const WaitlistCTA = ({ title, description }) => {
    const { email, setEmail, status, errorMessage, handleSubmit } = useWaitlist();

    return (
        <section className="py-32 px-4 relative overflow-hidden">
            <div className="max-w-4xl mx-auto text-center relative z-10">
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-5xl md:text-7xl font-medium tracking-tighter text-ink mb-6"
                >
                    {title}
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="text-xl text-ink/60 max-w-2xl mx-auto mb-12 font-light leading-relaxed"
                >
                    {description}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="max-w-md mx-auto"
                >
                    <AnimatePresence mode="wait" initial={false}>
                        {status === 'success' ? (
                            <motion.div
                                key="success"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3 }}
                                className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center"
                            >
                                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-200">
                                    <Check size={24} />
                                </div>
                                <h3 className="text-ink font-medium text-lg mb-1">You're on the list!</h3>
                                <p className="text-ink/60 text-sm">We'll notify you when access opens.</p>
                            </motion.div>
                        ) : (
                            <form
                                key="form"
                                onSubmit={handleSubmit}
                                className="relative flex flex-col items-center"
                            >
                                <div className="relative group w-full">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="email@address.com"
                                        disabled={status === 'loading'}
                                        className="w-full bg-white border border-black/10 rounded-full px-6 py-4 pr-36 text-ink placeholder:text-ink/30 outline-none focus:border-black/30 focus:shadow-sm transition-all disabled:opacity-50"
                                        required
                                    />
                                    <div className="absolute top-1.5 right-1.5 bottom-1.5">
                                        <Button
                                            type="submit"
                                            variant="dark"
                                            disabled={status === 'loading'}
                                            className="px-6 py-2.5 text-sm h-full disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {status === 'loading' ? (
                                                <Loader2 size={18} className="animate-spin" />
                                            ) : (
                                                <>
                                                    Join Waitlist <ArrowRight size={16} />
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                <div className="mt-4 text-center space-y-1">
                                    <p className="text-ink/60 text-sm font-medium">
                                        Limited spots for early access. No spam, ever.
                                    </p>
                                    <p className="text-ink/30 text-[10px] flex flex-col gap-1">
                                        <span>By joining, you agree to receive project updates from Fantazia. Unsubscribe anytime.</span>
                                        <a href="/privacy" className="underline hover:text-ink/50">Privacy Policy</a>
                                    </p>
                                </div>
                                {status === 'error' && (
                                    <p className="mt-3 text-red-600 text-sm text-center">
                                        {errorMessage}
                                    </p>
                                )}
                            </form>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
};

export default WaitlistCTA;
